import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputText } from '../../../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames, InputTextFormat, ModalVariants, InputRegexPattern } from '../../../../../../models/Common';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { ApiError } from '../../../../../../models/ApiError';
import { formatPhone, getErrorMessage } from '../../../../../../utils';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../../../store/reducers/ModalSlice';
import { FormInputPassword } from '../../../../../inputs/FormInputPassword';

interface AddNewSmsPhoneProps {
  modalTitle: string;
  description?: string;
}

const defaultProps = {
  description: '',
};

interface IEditSmsPhoneForm {
  phoneNumber: string;
  currentPassword: string;
}

export const EditSmsPhone: FC<AddNewSmsPhoneProps> = ({ modalTitle, description }) => {
  const { t } = useTranslation();
  const methods = useForm<IEditSmsPhoneForm>();
  const { handleSubmit, setError } = methods;
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [requestConfirmationSmsCodeApi, { isLoading }] = userInfoApi.useRequestConfirmationSmsCodeIsEditMutation();

  const handleRequestConfirmationSmsCode = async ({
    phoneNumber,
    currentPassword,
  }: {
    phoneNumber: string;
    currentPassword: string;
  }) => {
    try {
      await requestConfirmationSmsCodeApi({ phone: formatPhone(phoneNumber), currentPassword })
        .unwrap()
        .then(() => {
          dispatch(
            openModal({
              modalVariants: ModalVariants.ConfirmNewSmsPhone,
              content: { phone: formatPhone(phoneNumber) },
            }),
          );
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IEditSmsPhoneForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IEditSmsPhoneForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element === 'currentPassword' ? element : 'phoneNumber', {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{modalTitle}</article>
        {description && <p className="modals__header__description">{description}</p>}
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleRequestConfirmationSmsCode)}>
        <FormProvider {...methods}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />
          <FormInputText
            name="phoneNumber"
            className="primary"
            label={`${t('input.phone')}`}
            isRequired
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
            type="tel"
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.change')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </FormProvider>
      </form>
    </div>
  );
};

EditSmsPhone.defaultProps = defaultProps;
